import React, { useState } from 'react'
import { pantryColors, pantryRadii, pantryShadows } from '@pantry/ui'

interface AccordionCardProps {
  // DOM id on the card root (used by Page Score jump links).
  id?: string
  title: string
  subtitle?: string
  icon: React.ReactNode
  iconBg?: string
  iconColor?: string
  defaultOpen?: boolean
  headerRight?: React.ReactNode
  bodyPadding?: number | string
  children: React.ReactNode
}

export const AccordionCard: React.FC<AccordionCardProps> = ({
  id,
  title,
  subtitle,
  icon,
  iconBg,
  iconColor,
  defaultOpen = true,
  headerRight,
  bodyPadding = 20,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div
      id={id}
      data-accordion-card
      style={{
        background: pantryColors.white,
        border: `1px solid ${pantryColors.gray[200]}`,
        borderRadius: pantryRadii.lg,
        boxShadow: pantryShadows.sm,
        overflow: 'hidden',
        transition: 'box-shadow .15s',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = pantryShadows.md }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.boxShadow = pantryShadows.sm }}
    >
      <div
        role="button"
        data-accordion-header
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((o) => !o) }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 20px',
          cursor: 'pointer',
          userSelect: 'none',
          outline: 'none',
          transition: 'background .1s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = pantryColors.gray[50] }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent' }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: iconBg ?? pantryColors.brand.light,
            color: iconColor ?? pantryColors.brand.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: pantryColors.gray[800] }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: pantryColors.gray[400], marginTop: 1 }}>{subtitle}</div>}
        </div>
        {headerRight && <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>{headerRight}</div>}
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: pantryColors.gray[400],
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform .22s ease',
            flexShrink: 0,
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 15, height: 15 }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      <div
        style={{
          height: 1,
          background: pantryColors.gray[200],
        }}
      />

      <div
        style={{
          overflow: 'hidden',
          maxHeight: open ? 4000 : 0,
          transition: 'max-height .3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div style={{ padding: bodyPadding }}>{children}</div>
      </div>
    </div>
  )
}
